import { useState } from 'react'
import { Icon } from '@iconify/react'
import { Link } from 'react-router-dom'
import { Container, Reveal } from '@/components/ui'
import { useSlotT } from '@/cms/useSlotT'

export default function AboutSnippet() {
  const slotT = useSlotT('home')
  // `<object type="image/svg+xml">` works but eats the entire <img> image
  // error-handling pipeline: a 404 on /about-yada-core-values.svg leaves
  // the left half empty with no way for React to know. Switched to <img>
  // with onError so a missing file hides the frame and the gradient
  // background stays visible behind the decorative chips.
  const [svgFailed, setSvgFailed] = useState(false)

  return (
    <section className="py-section-y px-section-x bg-surface-2" aria-labelledby="about-snippet-title">
      <Container>
        <div className="grid lg:grid-cols-2 gap-8 lg:gap-12 items-center">
          {/* Image / visual side */}
          <Reveal>
            <div className="relative aspect-[4/3] max-h-[260px] sm:max-h-none sm:aspect-[4/3] rounded-yada-2xl bg-gradient-to-br from-primary via-primary-dark to-yada-olive overflow-hidden shadow-yada-xl">
              {!svgFailed ? (
                <img
                  src="/about-yada-core-values.svg"
                  alt="YADA 核心價值插畫：青年在學校、機構、企業的同行下被看見、被裝備、被賦能"
                  loading="lazy"
                  decoding="async"
                  // Force a render-size hint — <object> didn't, and the
                  // element had no intrinsic dimensions on browsers that
                  // treat SVGs via <object> async.
                  width={800}
                  height={600}
                  onError={() => setSvgFailed(true)}
                  className="absolute inset-0 w-full h-full object-cover"
                />
              ) : (
                // Soft fallback: the gradient + the two decorative chips
                // stay visible, so the card is not empty.
                <div className="absolute inset-0 flex items-center justify-center px-8 text-center">
                  <p className="text-surface/90 text-lg font-semibold">YADA · EdTech × Humanity</p>
                </div>
              )}
              <div className="absolute top-6 left-6 w-16 h-16 bg-accent rounded-yada flex items-center justify-center shadow-yada">
                <Icon icon="lucide:heart" className="text-ink text-3xl" />
              </div>
              <div className="absolute bottom-6 right-6 bg-surface px-4 py-2 rounded-yada shadow-yada">
                <p className="text-sm font-semibold text-ink">YADA · EdTech × Humanity</p>
              </div>
            </div>
          </Reveal>

          {/* Content */}
          <Reveal delay={0.1}>
            <span className="section-title__tagline inline-block mb-3">
              {slotT('home.about.tag')}
            </span>
            <h2
              id="about-snippet-title"
              className="text-2xl sm:text-3xl md:text-4xl font-bold text-ink mb-4 md:mb-6 leading-tight"
            >
              {slotT('home.about.title')}
            </h2>
            <p className="text-base sm:text-lg text-ink-soft leading-relaxed mb-6 text-balance">
              {slotT('home.about.text')}
            </p>
            {/* Match the Button primitive's primary/md class set so every CTA
                on the public site lines up identically. <Link> keeps
                client-side routing (an internal <a href> would full-reload
                the SPA, defeating React Router 7). */}
            <Link
              to="/about"
              className="inline-flex items-center justify-center gap-2 bg-primary text-surface px-6 py-3 rounded-yada-full hover:bg-primary-dark border-2 border-primary text-base font-semibold transition-all duration-200 hover:-translate-y-0.5 hover:shadow-yada-md focus-visible:outline-2 focus-visible:outline-primary focus-visible:outline-offset-2 w-full sm:w-auto"
            >
              <span>{slotT('home.about.cta')}</span>
              <Icon icon="lucide:arrow-right" />
            </Link>
          </Reveal>
        </div>
      </Container>
    </section>
  )
}
